import { Link } from 'react-router-dom';

function HowItWorks() {
  return (
    <div className="min-h-screen bg-puzzle-surface">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <h1 className="text-3xl font-bold text-puzzle-text mb-2">How It Works</h1>
        <p className="text-puzzle-text-secondary mb-8">Everything you need to know about Puzzlists</p>

        {/* Table of Contents */}
        <nav className="mb-10 p-4 bg-puzzle-background rounded-lg">
          <h2 className="font-semibold text-puzzle-text mb-3">Contents</h2>
          <ol className="list-decimal list-inside space-y-1 text-puzzle-blue text-sm">
            <li><a href="#daily-puzzle" className="hover:underline">Daily Puzzle</a></li>
            <li><a href="#how-to-play" className="hover:underline">How to Play</a></li>
            <li><a href="#create-puzzles" className="hover:underline">Creating Your Own Puzzles</a></li>
            <li><a href="#puzzle-rooms" className="hover:underline">Puzzle Rooms</a></li>
            <li><a href="#my-stats" className="hover:underline">My Stats</a></li>
            <li><a href="#account" className="hover:underline">Account & Settings</a></li>
            <li><a href="#leagues" className="hover:underline">Leagues</a></li>
          </ol>
        </nav>

        {/* Content */}
        <div className="space-y-8 text-puzzle-text">
          <section id="daily-puzzle" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">1. Daily Puzzle</h2>
            <p className="mb-3">
              Every day, a new puzzle is available for everyone to solve. The daily puzzle is
              the same for all players, making it a shared experience you can compare with friends.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>A new puzzle is released every day at midnight</li>
              <li>Complete daily puzzles to build your streak</li>
              <li>Compete on the global leaderboard for fastest time or fewest moves</li>
              <li>No account required to play, but sign in to save your streak</li>
            </ul>
          </section>

          <section id="how-to-play" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">2. How to Play</h2>
            <p className="mb-3">
              Puzzlists is a drag-and-drop jigsaw puzzle game. The goal is to reassemble
              the scrambled pieces into the complete image.
            </p>
            <h3 className="font-medium mt-4 mb-2">Controls</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Click and drag pieces to move them around the canvas</li>
              <li>Pieces will automatically snap together when correctly placed</li>
              <li>Connected pieces move together as a group</li>
              <li>Use the reference image toggle to see the completed puzzle</li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">Tips for Success</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Start with edge and corner pieces to build the frame</li>
              <li>Look for distinctive colors or patterns</li>
              <li>Group pieces with similar colors together before connecting</li>
              <li>Fewer moves means a better score on the leaderboard</li>
            </ul>
          </section>

          <section id="create-puzzles" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">3. Creating Your Own Puzzles</h2>
            <p className="mb-3">
              You can create custom puzzles from any image you want. This is perfect for
              turning your photos into puzzles to enjoy or share with friends.
            </p>
            <h3 className="font-medium mt-4 mb-2">How to Create a Puzzle</h3>
            <ol className="list-decimal list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>
                Go to the
                {' '}
                <Link to="/create" className="text-puzzle-blue hover:underline">Create Puzzle</Link>
                {' '}
                page
              </li>
              <li>Upload an image from your device</li>
              <li>Choose your puzzle size (number of pieces)</li>
              <li>Click Create to generate your puzzle</li>
            </ol>
            <p className="mt-3">
              Solo puzzles are saved to your account and can be replayed anytime.
              You can also share them with friends by creating a Puzzle Room.
            </p>
          </section>

          <section id="puzzle-rooms" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">4. Puzzle Rooms</h2>
            <p className="mb-3">
              Puzzle Rooms let you share custom puzzles with friends and compete on
              a private leaderboard. This is the best way to enjoy puzzles together!
            </p>
            <h3 className="font-medium mt-4 mb-2">Creating a Room</h3>
            <p className="mb-2 text-puzzle-text-secondary">
              After creating a puzzle, you will be prompted to create a room for it.
              You can customize several settings:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>
                <span className="text-puzzle-text">Room Name</span>
                {' '}
                - Give your room a custom name
              </li>
              <li>
                <span className="text-puzzle-text">Expiration</span>
                {' '}
                - Rooms automatically expire after 1 day or 1 week
              </li>
              <li>
                <span className="text-puzzle-text">Allow Guests</span>
                {' '}
                - Let players join without an account
              </li>
              <li>
                <span className="text-puzzle-text">Show Leaderboard</span>
                {' '}
                - Display rankings for completed players
              </li>
              <li>
                <span className="text-puzzle-text">Max Participants</span>
                {' '}
                - Limit how many people can join
              </li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">Sharing Your Room</h3>
            <p className="mb-2 text-puzzle-text-secondary">
              Once created, you will receive a unique invite link to share. Anyone with the
              link can join your room and attempt the puzzle.
            </p>
            <h3 className="font-medium mt-4 mb-2">Playing in a Room</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Each player has their own independent progress</li>
              <li>Your time and moves are tracked separately</li>
              <li>When you complete the puzzle, your stats appear on the room leaderboard</li>
              <li>The leaderboard can be sorted by time or number of moves</li>
              <li>You can replay the puzzle to improve your score</li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">Managing Your Rooms</h3>
            <p className="text-puzzle-text-secondary">
              View all your created rooms on the
              {' '}
              <Link to="/my-rooms" className="text-puzzle-blue hover:underline">My Rooms</Link>
              {' '}
              page. From there you can copy invite links, see player counts, and delete rooms.
            </p>
          </section>

          <section id="my-stats" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">5. My Stats</h2>
            <p className="mb-3">
              Track your lifetime puzzle statistics on the
              {' '}
              <Link to="/my-stats" className="text-puzzle-blue hover:underline">My Stats</Link>
              {' '}
              page. This feature requires a registered account.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>See your total puzzle completions across all puzzle types</li>
              <li>View completions broken down by Daily, Solo, and Room puzzles</li>
              <li>Track your total play time and total moves</li>
              <li>See your average time and moves per puzzle</li>
              <li>Monitor your daily streak progress</li>
            </ul>
            <p className="mt-3">
              Your stats persist even after puzzles or rooms expire or are deleted.
            </p>
          </section>

          <section id="account" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">6. Account & Settings</h2>
            <p className="mb-3">
              While you can play as a guest, creating an account unlocks additional features:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>Save your progress across devices</li>
              <li>Track your daily puzzle streak</li>
              <li>Create and manage Puzzle Rooms</li>
              <li>View your lifetime statistics</li>
              <li>Customize your profile with a display name and avatar</li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">Dark Mode</h3>
            <p className="text-puzzle-text-secondary">
              Prefer a darker interface? Toggle dark mode from the settings gear icon on any
              puzzle page, or from your
              {' '}
              <Link to="/account" className="text-puzzle-blue hover:underline">Account</Link>
              {' '}
              page under Game Settings.
            </p>
          </section>

          <section id="leagues" className="scroll-mt-28">
            <h2 className="text-xl font-semibold mb-3">7. Leagues</h2>
            <p className="mb-3">
              Leagues add a competitive ranked division system to Puzzlists. Climb through
              five tiers by earning rating points from daily puzzles.
            </p>
            <h3 className="font-medium mt-4 mb-2">The Five Tiers</h3>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>
                🥉
                <span className="text-puzzle-text">Bronze</span>
                {' '}
                &mdash; where everyone starts
              </li>
              <li>
                🥈
                <span className="text-puzzle-text">Silver</span>
              </li>
              <li>
                🥇
                <span className="text-puzzle-text">Gold</span>
              </li>
              <li>
                💎
                <span className="text-puzzle-text">Platinum</span>
              </li>
              <li>
                👑
                <span className="text-puzzle-text">Diamond</span>
                {' '}
                &mdash; the top tier
              </li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">How Rating Points Work</h3>
            <p className="mb-2 text-puzzle-text-secondary">
              Each daily puzzle completion earns rating points based on your performance.
              Your bi-weekly total determines your rank within your tier.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>
                <span className="text-puzzle-text">Speed score (0&ndash;50 pts)</span>
                {' '}
                &mdash; based on how fast you complete the puzzle.
                Par time is about 8 seconds per piece.
              </li>
              <li>
                <span className="text-puzzle-text">Efficiency score (0&ndash;50 pts)</span>
                {' '}
                &mdash; based on how few moves you use.
                Par is about 2.5 moves per piece.
              </li>
              <li>
                <span className="text-puzzle-text">Completion bonus (10 pts)</span>
                {' '}
                &mdash; awarded just for finishing
              </li>
              <li>
                <span className="text-puzzle-text">Streak multiplier (up to +20%)</span>
                {' '}
                &mdash; bonus for maintaining a daily streak (max at 10+ days)
              </li>
            </ul>
            <p className="mt-3 text-puzzle-text-secondary">
              <strong>Example:</strong>
              {' '}
              A fast player who finishes a 70-piece puzzle in 3 minutes but uses
              250 moves earns about 86 points. A methodical player who takes 12 minutes
              but only uses 90 moves earns roughly the same. Both playstyles are rewarded!
            </p>
            <h3 className="font-medium mt-4 mb-2">Bi-Weekly Promotion & Relegation</h3>
            <p className="mb-2 text-puzzle-text-secondary">
              Every two weeks (Sunday midnight UTC), players are ranked
              within their tier by total rating points:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 text-puzzle-text-secondary">
              <li>
                <span className="text-green-600">Top 20%</span>
                {' '}
                get
                {' '}
                <strong>promoted</strong>
                {' '}
                to the next tier up
              </li>
              <li>
                <span className="text-red-600">Bottom 20%</span>
                {' '}
                get
                {' '}
                <strong>relegated</strong>
                {' '}
                to the tier below
              </li>
              <li>Everyone else stays in their current tier</li>
              <li>Players with 0 completions for the cycle are automatically relegated</li>
              <li>Tiers need at least 5 players for promotion/relegation to apply</li>
            </ul>
            <h3 className="font-medium mt-4 mb-2">Getting Started</h3>
            <p className="text-puzzle-text-secondary">
              Visit the
              {' '}
              <Link to="/league" className="text-puzzle-blue hover:underline">Leagues</Link>
              {' '}
              page to join. You&apos;ll start in Bronze and can climb from there.
              An account is required to participate.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-puzzle-border">
          <p className="text-puzzle-text-secondary text-sm">
            Have questions? Use the feedback button to reach out to us.
            {' '}
            See also our
            {' '}
            <Link to="/about" className="text-puzzle-blue hover:underline">About</Link>
            {' '}
            page.
          </p>
        </div>
      </div>
    </div>
  );
}

export default HowItWorks;
